import { useEffect, useState } from "react";

import { useParams } from "react-router-dom";

import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { styled } from "simplydo/react/styled";
import { Icon, Message } from "simplydo/react/ui";

import api from "api";

import type { CompanyPdfCompany } from "components/innointel/types";
import { parseShareholders } from "components/innointel/types";

import CompanyAcquisitions from "./CompanyOverview/InfoComponents/CompanyAcquisitions";
import CompanyArticles from "./CompanyOverview/InfoComponents/CompanyArticles";
import CompanyBasicInfo from "./CompanyOverview/InfoComponents/CompanyBasicInfo";
import CompanyBlog from "./CompanyOverview/InfoComponents/CompanyBlog";
import CompanyCertifications from "./CompanyOverview/InfoComponents/CompanyCertifications";
import CompanyChildOwnerships from "./CompanyOverview/InfoComponents/CompanyChildOwnerships";
import CompanyCompaniesHouse from "./CompanyOverview/InfoComponents/CompanyCompaniesHouse";
import { CreditsafeReportSummary } from "./CompanyOverview/InfoComponents/CompanyCreditsafe";
import CompanyFundedResearch from "./CompanyOverview/InfoComponents/CompanyFundedResearch";
import CompanyMessages from "./CompanyOverview/InfoComponents/CompanyMessages";
import CompanyPatents from "./CompanyOverview/InfoComponents/CompanyPatents";
import CompanySanctions from "./CompanyOverview/InfoComponents/CompanySanctions";
import CompanyServices from "./CompanyOverview/InfoComponents/CompanyServices";
import CompanyShareholders from "./CompanyOverview/InfoComponents/CompanyShareholders";
import {
  HeaderContainer,
  HeaderInfo,
  InfoBox,
  InfoBoxWidget,
  InfoContainer,
  OverviewContainer,
} from "./CompanyOverview/Styles";

const CompanyPDFContainer = styled.div`
  background: #fff;
`;

const CompanyView = ({ company }: { company: CompanyPdfCompany }) => {
  const {
    images,
    categories = [],
    patents,
    articles = [],
    certifications,
    research = [],
    sources = {},
    messages = [],
    ai,
    news = [],
    crunchbase = {},
    creditsafe = {},
    blogs = [],
  } = company;
  const services = ai?.services || [];

  const { acquisitions = [], ownerships = [] } = crunchbase;
  const report = creditsafe?.report?.data;
  const shareholders = parseShareholders(
    report?.shareCapitalStructure?.shareHolders,
  );

  const hasShareholders = !!shareholders.length;
  const hasAcquisitions = !!acquisitions.length;
  const hasChildOwnerships = !!ownerships.length;
  const hasNews = !!(research.length || articles.length || news.length);
  const hasProtect = !!creditsafe?.protect?.alertsCount;
  const hasBlogs = !!blogs?.length;
  const hasMessages = !!messages.length;

  return (
    <CompanyPDFContainer>
      {/*
        Include link to font-awesome stylesheet here, otherwise any Semantic Icons do not render in the PDF download
      */}
      <link
        href="https://stackpath.bootstrapcdn.com/font-awesome/4.7.0/css/font-awesome.min.css"
        rel="stylesheet"
      />

      <InfoBoxWidget>
        <OverviewContainer>
          <HeaderContainer>
            <ImageWithFallback
              src={images?.logo?.url}
              fallbackElement={<Icon size="huge" name="building" />}
            />
            <HeaderInfo>
              <h3>Company</h3>
              <h1>{company.name}</h1>
              {categories.length ? (
                <span className="meta">{categories.join(", ")}</span>
              ) : null}
            </HeaderInfo>
          </HeaderContainer>
        </OverviewContainer>
      </InfoBoxWidget>

      <InfoBoxWidget style={{ marginLeft: 0 }}>
        <CompanyBasicInfo company={company} />
      </InfoBoxWidget>
      <InfoContainer $sectioned>
        <InfoBox>
          <InfoContainer>
            <CompanyServices services={services} />
          </InfoContainer>
          <InfoContainer>
            <CompanyCompaniesHouse company={company} isPDF />
          </InfoContainer>
          {creditsafe?.report ? (
            <InfoContainer>
              <InfoBoxWidget>
                <CreditsafeReportSummary creditsafe={creditsafe} />
              </InfoBoxWidget>
            </InfoContainer>
          ) : null}
          <InfoContainer>
            <CompanyCertifications certifications={certifications} />
          </InfoContainer>
          <InfoContainer>
            <CompanyPatents
              companyName={company.name ?? ""}
              patents={patents?.items}
              total={sources?.patents?.total}
            />
          </InfoContainer>
        </InfoBox>
      </InfoContainer>

      {hasShareholders || hasAcquisitions || hasChildOwnerships ? (
        <InfoContainer>
          <InfoBox>
            <InfoContainer>
              <InfoBoxWidget>
                <CompanyShareholders shareholders={shareholders} isPDF />
              </InfoBoxWidget>
            </InfoContainer>
            <InfoContainer>
              <CompanyChildOwnerships childOwnerships={ownerships} />
            </InfoContainer>
            <InfoContainer>
              <CompanyAcquisitions acquisitions={acquisitions} />
            </InfoContainer>
          </InfoBox>
        </InfoContainer>
      ) : null}

      {hasProtect ? (
        <InfoContainer>
          <InfoBox>
            <InfoContainer>
              <CompanySanctions protect={creditsafe?.protect} />
            </InfoContainer>
          </InfoBox>
        </InfoContainer>
      ) : null}

      {hasNews ? (
        <InfoContainer>
          <InfoBox>
            <InfoContainer>
              <CompanyFundedResearch fundingData={research} />
            </InfoContainer>
            <InfoContainer>
              <CompanyArticles
                numArticles={crunchbase.numArticles}
                articles={articles}
                news={news}
              />
            </InfoContainer>
          </InfoBox>
        </InfoContainer>
      ) : null}

      {hasBlogs ? (
        <InfoContainer>
          <InfoBox>
            <InfoContainer>
              <CompanyBlog blogs={blogs} />
            </InfoContainer>
          </InfoBox>
        </InfoContainer>
      ) : null}

      {hasMessages ? (
        <InfoContainer>
          <InfoBox>
            <InfoContainer>
              <CompanyMessages messages={messages} />
            </InfoContainer>
          </InfoBox>
        </InfoContainer>
      ) : null}
    </CompanyPDFContainer>
  );
};

const CompanyPDF = () => {
  const [company, setCompany] = useState<CompanyPdfCompany | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  const companyId = useParams().companyId;

  useEffect(() => {
    if (!companyId) {
      return;
    }
    api.token = api.token || "magic"; // For the actual pdf request we inject the authorization header manually
    api.innointel.getCompany(
      companyId,
      (response) => {
        setCompany(response.company as CompanyPdfCompany);
        if (api.token === "magic") {
          api.token = null;
        }
      },
      (err) => {
        setErrorMessage(err.message);
        if (api.token === "magic") {
          api.token = null;
        }
      },
    );
  }, [companyId]);

  if (errorMessage) {
    return (
      <Message error>
        <p>{errorMessage} </p>
      </Message>
    );
  }
  if (!company) {
    return (
      <Message info>
        {errorMessage}, {companyId}
      </Message>
    );
  }
  return <CompanyView company={company} />;
};

export default CompanyPDF;
